import { backendUrl } from './variables';

// Helper function to check if Firebase is available
const isFirebaseAvailable = () => {
  try {
    // eslint-disable-next-line no-undef
    return typeof firebase !== 'undefined' && firebase !== null;
  } catch (e) {
    return false;
  }
};

// Helper function to detect Safari
const isSafari = () => {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
};

// Helper function to check if notifications are supported
const isNotificationSupported = () => {
  return typeof Notification !== 'undefined' && 
         'serviceWorker' in navigator && 
         'PushManager' in window;
};

class FirebaseNotificationService {
  constructor() {
    this.supported = isNotificationSupported();
    this.isSafari = isSafari();
    this.registration = null;
    this.messaging = null;
    this.firebase = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (!this.supported) {
      console.log('Push notifications not supported');
      return false;
    }
    
    // Safari-specific handling
    if (this.isSafari) {
      console.log('Safari detected - push notifications may have limited functionality');
    }
    
    try {
      console.log('Initializing Firebase notification service...');
      
      // Load Firebase SDK if not already loaded
      if (!isFirebaseAvailable()) {
        await this._loadFirebaseSDK();
      }
      
      // Initialize Firebase
      const firebaseConfig = {
        apiKey: "AIzaSyA_6GxMMKyYG02IS8rUlv8Z6TZiDrpyG4w",
        authDomain: "clowza-app.firebaseapp.com",
        projectId: "clowza-app",
        storageBucket: "clowza-app.firebasestorage.app",
        messagingSenderId: "835944366773",
        appId: "1:835944366773:web:659945bbd0314f1b35f4fc",
        measurementId: "G-588JNQJ5Q4"
      };
      
      // Check if Firebase app is already initialized
      let app;
      try {
        // eslint-disable-next-line no-undef
        app = firebase.app();
      } catch (e) {
        // App not initialized, create it
        // eslint-disable-next-line no-undef
        app = firebase.initializeApp(firebaseConfig);
      }
      
      this.firebase = app;
      // eslint-disable-next-line no-undef
      this.messaging = firebase.messaging();
      
      // Register Firebase service worker
      try {
        // Check if Firebase service worker is already registered
        const registrations = await navigator.serviceWorker.getRegistrations();
        let existingRegistration = null;
        
        for (const registration of registrations) {
          if (registration.active && registration.active.scriptURL.includes('firebase-messaging-sw.js')) {
            existingRegistration = registration;
            break;
          }
        }
        
        if (existingRegistration) {
          this.registration = existingRegistration;
          console.log('Using existing Firebase service worker');
        } else {
          // Register new service worker
          this.registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { 
            scope: '/'
          });
          console.log('Firebase service worker registered');
        }
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        console.log('Service worker is ready');
        
        // Ensure the service worker is active
        if (!this.registration.active) {
          throw new Error('Service worker failed to activate');
        }
        
        this.isInitialized = true;
        return true;
      } catch (swError) {
        console.error('Service worker registration failed:', swError);
        throw new Error(`Service worker registration failed: ${swError.message}`);
      }
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      return false;
    }
  }

  async _loadFirebaseSDK() {
    return new Promise((resolve, reject) => {
      // Check if Firebase is already loaded
      if (isFirebaseAvailable()) {
        resolve();
        return;
      }

      // Load Firebase App SDK
      const appScript = document.createElement('script');
      appScript.src = 'https://www.gstatic.com/firebasejs/9.0.0/firebase-app-compat.js';
      appScript.onload = () => {
        // Load Firebase Messaging SDK
        const messagingScript = document.createElement('script');
        messagingScript.src = 'https://www.gstatic.com/firebasejs/9.0.0/firebase-messaging-compat.js';
        messagingScript.onload = () => {
          // Wait a bit to ensure Firebase is fully initialized
          setTimeout(() => {
            if (isFirebaseAvailable()) {
              resolve();
            } else {
              reject(new Error('Firebase failed to load properly'));
            }
          }, 500);
        };
        messagingScript.onerror = reject;
        document.head.appendChild(messagingScript);
      };
      appScript.onerror = reject;
      document.head.appendChild(appScript);
    });
  }

  async requestPermission() {
    if (!this.supported) return 'denied';
    if (typeof Notification === 'undefined') return 'denied';
    return await Notification.requestPermission();
  }

  async getFCMToken() {
    try {
      if (!this.isInitialized) {
        console.log('Initializing Firebase...');
        await this.initialize();
      }
      
      if (!this.messaging) {
        throw new Error('Firebase messaging not initialized');
      }

      console.log('Requesting notification permission...');
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      // Ensure service worker is ready
      if (!this.registration || !this.registration.active) {
        console.log('Service worker not ready, waiting...');
        await this.initialize();
      }

      // Safari-specific handling for FCM token
      if (this.isSafari) {
        console.log('Safari detected - FCM token generation may take longer');
      }

      console.log('Getting FCM token...');
      
      // Add retry logic for token generation
      let token = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!token && retryCount < maxRetries) {
        try {
          // Try with service worker registration first
          if (this.registration && this.registration.active) {
            token = await this.messaging.getToken({
              vapidKey: 'BFu6gMtfvrgCgHGu9pkD70YwuXPmtZg1OhZsVfaRc-m8OAs-SwxKAOuazvCuTsHUlwkdWnn2isxniJDBAqaWnSg',
              serviceWorkerRegistration: this.registration
            });
          } else {
            // Fallback: try without service worker registration
            console.log('Trying token generation without service worker registration...');
            token = await this.messaging.getToken({
              vapidKey: 'BFu6gMtfvrgCgHGu9pkD70YwuXPmtZg1OhZsVfaRc-m8OAs-SwxKAOuazvCuTsHUlwkdWnn2isxniJDBAqaWnSg'
            });
          }
          
          if (token) {
            break;
          }
        } catch (tokenError) {
          console.warn(`Token generation attempt ${retryCount + 1} failed:`, tokenError);
          
          // Handle specific pushManager error
          if (tokenError.message && tokenError.message.includes('pushManager')) {
            console.log('PushManager error detected, trying alternative approach...');
            // Force re-initialization on next attempt
            this.isInitialized = false;
          }
          
          retryCount++;
          
          if (retryCount < maxRetries) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
      }
      
      if (!token) {
        throw new Error('No FCM token available after multiple attempts');
      }
      
      console.log('FCM token obtained successfully via getFCMToken');
      return token;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      
      // Safari-specific error handling
      if (this.isSafari && error.message.includes('permission')) {
        throw new Error('Safari requires manual permission for notifications. Please enable notifications in Safari settings.');
      }
      
      throw error;
    }
  }

  async subscribe(userEmail, user_id) {
    try {
      const fcmToken = await this.getFCMToken();
      //console.log('FCM token obtained successfully via subscribe');
      // Send FCM token to backend
      if (user_id) {
        await this._sendFCMTokenToBackend(fcmToken, userEmail, user_id);
      }
      
      return { fcmToken, type: 'firebase' };
    } catch (error) {
      console.error('Firebase subscription failed:', error);
      throw error;
    }
  }

  async _sendFCMTokenToBackend(fcmToken, userEmail, user_id) {
    try {
      console.log('Sending FCM token to backend...');
      
      const response = await fetch(`${backendUrl}/save_fcm_token.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user_id,
          email: userEmail,
          fcm_token: fcmToken,
          device_type: 'web'
        })
      });

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to save FCM token');
      }

      console.log('FCM token saved to backend successfully');
    } catch (error) {
      console.error('Failed to send FCM token to backend:', error);
      throw error;
    }
  }

  async unsubscribe() {
    try {
      if (this.messaging) {
        // Delete FCM token
        await this.messaging.deleteToken();
        console.log('FCM token deleted');
      }
      
      if (this.registration) {
        // Unregister service worker
        await this.registration.unregister();
        console.log('Service worker unregistered');
      }
      
      this.isInitialized = false;
      this.messaging = null;
      this.registration = null;
      
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  }

  getPermissionStatus() {
    if (!this.supported) return 'denied';
    if (typeof Notification === 'undefined') return 'denied';
    return Notification.permission;
  }

  // Handle foreground messages
  onMessage(callback) {
    if (this.messaging) {
      this.messaging.onMessage(callback);
    }
  }
}

// Create and export a singleton instance
const firebaseNotificationService = new FirebaseNotificationService();
export default firebaseNotificationService;
